import { Button, Card, CardContent, CardHeader, CardTitle } from '@folio/components';
import { useToast } from '@folio/components/ui/use-toast';
import { formatDate, useAssertedParams } from '@folio/lib';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerListDocumentsQueryOptions,
  keyValueFieldsToObject,
  useBrokerageFirmControllerGenerateDocumentsForSignature,
  useBrokerageFirmControllerGenerateSignatureRequest,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Text } from '@folio/components/ui/text';

export function ResendPartnershipDocumentModule() {
  const { t } = useTranslation();
  const { brokerageFirmId } = useAssertedParams();
  const { toast } = useToast();

  const { data: lastSignedAt } = useQuery({
    ...brokerageFirmControllerListDocumentsQueryOptions(brokerageFirmId, {
      pageInfo: { count: 100 },
      includeArchived: true,
    }),
    select: data => {
      const signedContracts = data.data
        .map(doc => ({ ...doc, metadata: keyValueFieldsToObject(doc.metadata) }))
        .filter(
          doc =>
            doc.category === 'DISTRIBUTION_CONTRACT' &&
            doc.metadata.signed === 'true' &&
            typeof doc.metadata.whenat === 'string',
        )
        .map(doc => new Date(doc.metadata.whenat as string))
        .sort((a, b) => b.valueOf() - a.valueOf());
      return signedContracts[0];
    },
  });

  const { data: hasProductSentForSignature = false } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(brokerageFirmId, {
      statuses: ['WAITING_FOR_SIGNATURE', 'APPROVED'],
    }),
    select: data => data.data.length > 0,
  });

  const { mutateAsync: generateDocumentsForSignature } =
    useBrokerageFirmControllerGenerateDocumentsForSignature();
  const { mutateAsync: generateSignatureRequest } =
    useBrokerageFirmControllerGenerateSignatureRequest();

  const { mutate: resend, isPending } = useMutation({
    mutationFn: async () => {
      const { ids } = await generateDocumentsForSignature({
        brokerageFirmId,
        params: { documentTypes: ['DISTRIBUTION_CONTRACT'], includeApproved: true },
      });
      if (ids.length === 0) {
        throw new Error('NO_DOCUMENT_GENERATED');
      }
      await generateSignatureRequest({ brokerageFirmId, data: { deliveryMode: 'EMAIL' } });
    },
    onSuccess: () => {
      toast({ description: t('resendPartnershipDocument.success') });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('resendPartnershipDocument.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Text size="sm" tone="muted" className="mb-2">
          {t('resendPartnershipDocument.description')}
        </Text>
        {lastSignedAt && (
          <Text size="xs" tone="muted" className="mb-4">
            {t('resendPartnershipDocument.lastSignedAt', { date: formatDate(lastSignedAt) })}
          </Text>
        )}
        {!hasProductSentForSignature && (
          <Text size="xs" tone="muted" className="mb-4">
            {t('resendPartnershipDocument.disabledHint')}
          </Text>
        )}
        <Button onClick={() => resend()} disabled={isPending || !hasProductSentForSignature}>
          {isPending
            ? t('resendPartnershipDocument.sending')
            : t('resendPartnershipDocument.button')}
        </Button>
      </CardContent>
    </Card>
  );
}
